import React, { useRef, useEffect } from "react";

function PlaylistDrawer({ open, tracks, onClose, currentTrack, isPlaying, onRemoveTrack, onPlayTrack }) {
  const drawerRef = useRef(null);
  
  // Enable touch gestures for mobile (swipe down to close)
  useEffect(() => {
    if (!drawerRef.current) return;
    
    let startY = 0;
    let currentY = 0;
    const drawer = drawerRef.current;
    
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e) => {
      if (!open) return;
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;
      
      // Only allow dragging down, not up
      if (diff > 0) {
        drawer.style.transform = `translateY(${diff}px)`;
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = () => {
      drawer.style.transform = '';
      if (currentY - startY > 100) {
        onClose();
      }
    };
    
    drawer.addEventListener('touchstart', handleTouchStart);
    drawer.addEventListener('touchmove', handleTouchMove, { passive: false });
    drawer.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      drawer.removeEventListener('touchstart', handleTouchStart);
      drawer.removeEventListener('touchmove', handleTouchMove);
      drawer.removeEventListener('touchend', handleTouchEnd);
    };
  }, [open, onClose]);

  // Handle track removal
  const handleRemoveTrack = (track, event) => {
    event.stopPropagation();
    
    if (!track || !track.id) {
      console.log("Invalid track object, missing ID:", track);
      return;
    }
    
    console.log(`Removing track ID: ${track.id}, Title: ${track.attributes?.name}`);
    
    if (typeof onRemoveTrack !== 'function') {
      console.log("onRemoveTrack is not a function:", onRemoveTrack);
      return;
    }
    
    onRemoveTrack(track);
  };

  // Handle playing a track
  const handlePlayTrack = (track) => {
    if (typeof onPlayTrack === 'function') {
      onPlayTrack(track);
    }
  };

  return (
    <div className={`playlist-drawer ${open ? "open" : ""}`} ref={drawerRef}>
<div className="playlist-handle" onClick={() => {
  console.log("Handle clicked");
  // Check if drawer has "open" class directly
  if (drawerRef.current.classList.contains("open")) {
    console.log("Drawer is open, closing it");
    // Remove the class directly and also call onClose for React state
    drawerRef.current.classList.remove("open");
    onClose();
  } else {
    console.log("Drawer is closed, opening it");
    // Add the class directly
    drawerRef.current.classList.add("open");
  }
}}></div>
      <div className="playlist-header">
        <h2 className="playlist-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18V5L21 3V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Your Playlist
        </h2>
       <button className="btn-icon" onClick={() => {
  console.log("Close button clicked");
  // Directly manipulate DOM and call React handler
  if (drawerRef.current) {
    drawerRef.current.classList.remove("open");
  }
  onClose();
}} aria-label="Close playlist">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
</button>
      </div>
      
      <div className="playlist-content">
        {tracks.length === 0 ? (
          <div className="playlist-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.5, margin: '0 auto 1rem', display: 'block' }}>
              <path d="M9 18V5L21 3V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p>Your playlist is empty</p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Search for songs to add to your playlist</p>
          </div>
        ) : (
          <div>
            {tracks.map((track, i) => {
              const isCurrentTrack = currentTrack && currentTrack.id === track.id;
              
              return (
                <div key={i} className={`playlist-item ${isCurrentTrack ? 'now-playing-item' : ''}`} 
                     onClick={() => handlePlayTrack(track)}>
                  <img 
                    className="playlist-item-artwork playlist-artwork-circle" 
                    src={track.attributes.artwork.url.replace("{w}x{h}", "96x96")} 
                    alt={`${track.attributes.name} artwork`} 
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'><circle cx='12' cy='12' r='10'/><path d='M12 8v4M12 16h.01'/></svg>";
                    }}
                  />
                  
                  <div className="playlist-item-info">
                    <div className="playlist-item-title">
                      {isCurrentTrack && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" 
                             style={{ marginRight: '6px', color: 'var(--primary)' }}>
                          {isCurrentTrack && isPlaying ? (
                            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1"/>
                          ) : (
                            <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" 
                              fill="currentColor" />
                          )}
                          <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1" />
                        </svg>
                      )}
                      {track.attributes.name}
                    </div>
                    <div className="playlist-item-artist">{track.attributes.artistName}</div>
                  </div>
                  
                  <div className="playlist-item-actions">
                    {isCurrentTrack && (
                      <button 
                        className="playlist-play-btn" 
                        aria-label={isPlaying ? "Pause" : "Play"}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Re-play the same track to toggle state
                          handlePlayTrack(track);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '0',
                          cursor: 'pointer',
                          color: '#000',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '8px'
                        }}
                      >
                        {isPlaying ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>
                            <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 3L19 12L5 21V3Z" fill="currentColor"/>
                          </svg>
                        )}
                      </button>
                    )}
                    
                    <button 
                      className="playlist-delete-btn" 
                      aria-label="Remove from playlist"
                      onClick={(e) => handleRemoveTrack(track, e)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlaylistDrawer;